<template>
  <div class="animated fadeIn">
    <b-row class="mt-3">
      <b-col>
        <b-form-group>
          <b-form-input
            id="SearchText"
            name="SearchText"
            v-model="searchText"
            type="text"
            @input="debouncedOnChange"
            placeholder="Search across all fields"
            aria-describedby="search-text-feedback"
            autocomplete="off"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="12">
        <b-card>
          <b-card-header>
            <b-row>
              <b-col>
                <h4>{{ this.selectedRuleType === "DenialCodeRule" ? "Denial Status" : "EDI Error" }} Mapping Records</h4>
              </b-col>
              <b-col>
                <div class="pull-right">
                  <h5>Total Records: {{searchResults.total}}</h5>
                </div>
              </b-col>
            </b-row>
          </b-card-header>
          <b-row>
            <b-col lg="6">
              <b-form-select v-model="selectedRuleType" v-on:change="getDenialType" class="mb-3" :options=ruleTypeOptions>
              </b-form-select>
            </b-col>
          </b-row>
          <b-table
            striped
            hover
            :fields="fields"
            :items="searchResults.results"
            @sort-changed="changeSorting"
            :sort:by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            no-local-sorting
            small
          >
            <template slot="denial_code" slot-scope="data">
              <div class="denial-code-col">
                <a href @click.prevent="itemSelected(data.item)">{{ data.value }}</a>
              </div>
            </template>
            <template slot="denial_reason" slot-scope="data">
              <div class="denial-reason-col">
                <a href @click.prevent="itemSelected(data.item)">{{ data.value }}</a>
              </div>
            </template>
            <template slot="sub_status_text" slot-scope="data">
              <div class="sub-status-col">{{ getStatus(data.item) }}</div>
            </template>
            <template slot="retro_applied_date" slot-scope="data">
              <div class="denial-code-col">{{ formatDate(data.value) }}</div>
            </template>
            <template slot="delete_button" slot-scope="data">
              <div class="delete-col">
                <a href @click.prevent="deleteItem(data.item)">Delete</a>
              </div>
            </template>
          </b-table>
          <b-pagination
            align="center"
            :total-rows="rows"
            :per-page="size"
            v-model="currentPage"
            @input="changePage"
          ></b-pagination>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="2" offset-sm="10">
        <b-button class="mb-3" variant="primary" block v-on:click="create()">Add New {{ this.selectedRuleType === "DenialCodeRule" ? "Denial Code" : "EDI" }} Map</b-button>
      </b-col>
    </b-row>
    <b-modal
      id="editDenialCodeMap"
      ref="editDenialCodeMap"
      centered
      size="lg"
      @ok="handleOk"
      title="Add/Edit Mapping"
      :busy="isBusy"
    >
      <b-form v-if="currentRow" @submit.stop.prevent="handleSubmit">
        <b-row>
          <b-col>
            <b-row>
              <b-col>
                <b-form-group>
                  <label for="name">{{ this.selectedRuleType === "DenialCodeRule" ? "Denial" : "EDI Error" }} Code</label>
                  <b-form-input
                    id="inputDenialCode"
                    name="inputDenialCode"
                    v-model="currentRow.denial_code"
                    type="text"
                    class="mb-1"
                    aria-describedby="denial-code-feedback"
                    autocomplete="off"
                    v-validate="this.selectedRuleType === 'DenialCodeRule' ? 'length:2|alpha' : ''"
                    data-vv-as="Denial Code"
                    :class="{'input': true, 'text-danger': errors.has('inputDenialCode') }"
                  />

                  <div
                    class="text-danger"
                    v-show="errors.has('inputDenialCode')"
                  >{{ errors.first('inputDenialCode') }}</div>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group>
                  <label for="name">Action*</label>
                  <b-form-select
                    id="dropdownSubStatusText"
                    name="dropdownSubStatusText"
                    class="sub-status-dropdown"
                    v-model="actionDropDownList"
                    data-vv-as="Action"
                    v-validate="'required'"
                    v-on:change="getSelectedItem"
                    :options="dropDownListByType"
                  ></b-form-select>
                  <div
                    class="text-danger"
                    v-show="errors.has('dropdownSubStatusText')"
                  >{{ errors.first('dropdownSubStatusText') }}</div>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-form-group>
                  <label for="name">{{ this.selectedRuleType === "DenialCodeRule" ? "Denial Reason" : "EDI Error Description" }}</label>
                  <b-form-textarea
                    id="inputDenialReason"
                    name="inputDenialReason"
                    data-vv-as="Denial Reason"
                    v-model="currentRow.denial_reason"
                    type="text"
                    class="mb-2"
                    aria-describedby="denial_reason-feedback"
                    autocomplete="off"
                    :class="{'input': true, 'text-danger': errors.has('inputDenialReason') }"
                  />
                  <div
                    class="text-danger"
                    v-show="errors.has('inputDenialReason')"
                  >{{ errors.first('inputDenialReason') }}</div>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                  <b-form-group>
                    <b-form-checkbox
                      id="checkBoxRetro"
                      name="checkBoxRetro"
                      v-model="currentRow.apply_retro"
                    >
                    {{ checkBoxText }}
                    </b-form-checkbox>
                  </b-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
    <b-modal
      id="deleteConfirmation"
      ref="deleteConfirmation"
      centered
      size="md"
      @ok="confirmDelete"
      title="Delete Confirmation"
    >
      <p>Are you sure you would like to delete this record:</p>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col sm="4">{{ this.selectedRuleType === "DenialCodeRule" ? "Denial" : "EDI Error" }} Code</b-col>
        <b-col>{{deleteRow.denial_code}}</b-col>
      </b-row>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col sm="4">{{ this.selectedRuleType === "DenialCodeRule" ? "Denial Reason" : "EDI Error Descrption" }}</b-col>
        <b-col>{{deleteRow.denial_reason}}</b-col>
      </b-row>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col sm="4">Action</b-col>
        <b-col>{{ getStatus(deleteRow) }}</b-col>
      </b-row>
    </b-modal>
    <b-modal
      id="retroConfirmation"
      ref="retroConfirmation"
      centered
      size="md"
      @ok="handleSubmit"
      title="Retro Confirmation"
    >
      <p>Are you sure you want this rule to be applied retroactively?</p>
    </b-modal>
  </div>
</template>

<script>
import {
  PA_SAVE_DENIAL_CODE_MAP,
  PA_SEARCH_DENIAL_CODE_MAP,
  PA_DELETE_DENIAL_CODE_MAP
} from "@/store/denial-code-map/actions.type";

import * as _ from "lodash";

import { mapState } from "vuex";
import Vue from 'vue';
import moment from "moment";
export default {
  name: "DenialCodeMap",
  data() {
    return {
      currentPage: 1,
      searchText: "",
      size: 20,
      sortBy: "denial_code",
      sortDesc: false,
      currentRow: null,
      deleteRow: null,
      checkBoxText: "Apply rule retroactively to all claims",
      selectedRuleType: "DenialCodeRule",
      ruleTypeOptions: [
        { value: "DenialCodeRule", text: "Denial Status" },
        { value: "EDIRule", text: "EDI Error" }
      ]
    };
  },
  computed: {
    ...mapState({
      isBusy: state => state.denialcodemap.isBusy,
      searchResults: state => state.denialcodemap.searchResults
    }),
    rows() {
      return this.searchResults.total;
    },
    modifiedFieldList() {
      return this.dataFields.filter(x => x.selected === true);
    },
    actionDropDownList: {
      get: function() {
        if (this.currentRow.reattempt_action_type !== "") {
          return this.currentRow.reattempt_action_type;
        } else {
          return this.currentRow.sub_status_text;
        }
      },
      set: function(value) {
        Vue.set(this.currentRow, "sub_status_text", value);
      }
    },
    dropDownListByType: {
      get: function() {
        if (this.selectedRuleType === "EDIRule") {
          return [
            { value: "standard", text: "Retry Original Delivery" },
            { value: "portal", text: "Retry to Portal" }
          ];
        }

        return [
        { value: "PADeniedNoAttempt", text: "Set SubStatus to PA Canceled" },
        { value: "PAApproved", text: "Set SubStatus to PA Approved" },
        { value: "PADenied", text: "Set SubStatus to PA Denied" },
        { value: "standard", text: "Retry Original Delivery" },
        { value: "portal", text: "Retry to Portal" }
        ];
      }
    },
    fields: {
      get: function() {
        if (this.selectedRuleType === "EDIRule") {
          return [
            {
              key: "denial_code",
              label: "EDI Error Code",
              sortable: true
            },
            {
              key: "denial_reason",
              label: "EDI Error Description",
              sortable: true
            },
            {
              key: "sub_status_text",
              label: "Action",
              sortable: true
            },
            {
              key: "retro_applied_date",
              label: "Retro Applied",
              sortable: true
            },
            {
              key: "delete_button",
              label: "",
              sortable: false
            }
          ];
        }
        return [
          {
            key: "denial_code",
            label: "Denial Code",
            sortable: true
          },
          {
            key: "denial_reason",
            label: "Denial Reason",
            sortable: true
          },
          {
            key: "sub_status_text",
            label: "Action",
            sortable: true
          },
          {
            key: "retro_applied_date",
            label: "Retro Applied",
            sortable: true
          },
          {
            key: "delete_button",
            label: "",
            sortable: false
          }
        ];
      }
    }
  },
  methods: {
    changeSearch() {
      this.currentPage = 1;
      this.search();
    },
    changeSorting(ctx) {
      this.sortDesc = ctx.sortDesc;
      this.sortBy = ctx.sortBy;
      this.search();
    },
    changePage(page) {
      this.currentPage = page;
      this.search();
    },
    isSuccess() {
      if (this.message === "Success") {
        this.showDismissibleAlert = true;
        return true;
      }
      this.showDismissibleAlert = false;
      return false;
    },
    onXClick(name) {
      // this.$store.dispatch(PA_CREATE_DATAOVERRIDE_CLEAR_MESSAGE);
      var item = this.dataFields.find(obj => obj.name === name);

      if (item !== undefined) {
        item.selected = false;
        item.value = "";
      }
      this.selectedFieldItem = null;
    },
    async handleSubmit() {
      const result = await this.$validator.validateAll();
      if (!result) {
        return;
      }

      if (this.currentRow.sub_status_text === "standard" || this.currentRow.sub_status_text === "portal") {
        this.currentRow.reattempt_action_type = this.currentRow.sub_status_text;
        this.currentRow.sub_status_text = "";
      } else {
        this.currentRow.reattempt_action_type = "";
      }

      if (!this.$store.state.isBusy) {
        await this.$store.dispatch(PA_SAVE_DENIAL_CODE_MAP, this.currentRow);
        // timeout to allow Elasticsearch time to complete writing record.
        // For some reason after save the search still returns old record
        setTimeout(() => this.search(), 1000);
        this.$refs.editDenialCodeMap.hide();
      }
    },
    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault();
      if (this.currentRow.apply_retro === true) {
        this.$refs.retroConfirmation.show();
        return;
      }
      // Trigger submit handler
      this.handleSubmit();
    },
    search() {
      if (!this.$store.state.da.isSearchBusy) {
        let criteria = {
          search_text: this.searchText,
          page: this.currentPage,
          size: this.size,
          sort_desc: this.sortDesc,
          sort_by: this.sortBy,
          mapped_results_only: true,
          rule_type: this.selectedRuleType
        };
        this.$store.dispatch(PA_SEARCH_DENIAL_CODE_MAP, criteria);
      }
    },
    itemSelected(item) {
      this.currentRow = { ...item };
      Vue.set(this.currentRow, "apply_retro", false);
      if (this.currentRow.reattempt_action_type === undefined) {
        Vue.set(this.currentRow, "reattempt_action_type", "");
      } else if (this.currentRow.sub_status_text === undefined || this.currentRow.sub_status_text === "") {
        Vue.set(this.currentRow, "sub_status_text", this.currentRow.reattempt_action_type);
      }
      this.$refs.editDenialCodeMap.show();
    },
    deleteItem(item) {
      this.deleteRow = item;
      this.$refs.deleteConfirmation.show();
    },
    getStatus(data) {
      if (data.sub_status_text !== "") {
        return data.sub_status_text === "PADeniedNoAttempt"
          ? "PA Canceled"
          : data.sub_status_text === "PADenied"
          ? "PA Denied"
          : data.sub_status_text === "PAApproved"
          ? "PA Approved"
          : "";
      } else if (data.reattempt_action_type !== "") {
        return data.reattempt_action_type === "standard"
        ? "Retry Original Delivery"
        : data.reattempt_action_type === "portal"
        ? "Retry to Portal"
        : "";
      }
      return "";
    },
    async confirmDelete() {
      await this.$store.dispatch(PA_DELETE_DENIAL_CODE_MAP, this.deleteRow);
      this.deleteRow = null;
      // timeout to allow Elasticsearch time to complete writing record.
      // For some reason after save the search still returns old record
      setTimeout(() => this.search(), 1000);
    },
    create() {
      this.currentRow = {
        denial_code_map_id: "",
        denial_code: "",
        denial_reason: "",
        sub_status_text: "",
        reattempt_action_type: "",
        apply_retro: "false",
        rule_type: this.selectedRuleType
      };
      this.$refs.editDenialCodeMap.show();
    },
    formatDate(data) {
      const formattedDate = moment.utc(data).local().format("M/DD/YYYY h:mm:ss a");
      if (formattedDate === "Invalid date") return "";
      return formattedDate;
    },
    getSelectedItem(newValue) {
      switch (newValue) {
        case "":
        case "PADeniedNoAttempt":
        case "PAApproved":
        case "PADenied":
          if (this.currentRow.denial_code_map_id === "") {
            Vue.set(this.currentRow, "apply_retro", false);
          }
          this.checkBoxText = "Apply rule retroactively to all claims";
          break;
        case "standard":
        case "portal":
          if (this.currentRow.denial_code_map_id === "") {
            Vue.set(this.currentRow, "apply_retro", true);
          }
          this.checkBoxText = "Apply rule retroactively to all claims (retro of retry rule limited to past 7 days)";
          break;
      }
    },
    getDenialType: function(ruleType) {
      this.selectedRuleType = ruleType;
      this.search();
    }
  },
  created() {
    this.debouncedOnChange = _.debounce(this.changeSearch, 500);
  },
  beforeMount() {
    this.search();
  }
};
</script>

<style scoped>
.success-label {
  word-break: break-all;
}

.mt-10 {
  margin-top: 10px !important;
}
.sub-status-col {
  width: 150px;
}
.denial-code-col {
  width: 120px;
}
.delete-col {
  width: 100px;
}
</style>



// WEBPACK FOOTER //
// src/views/DenialCodeMap.vue